import "@/styles/globals.css";
// Configures i18next before the first render, so `useT()` is synchronous and
// there is no flash of untranslated text. Must come before any component import
// that calls a translation hook at module scope.
import "@/lib/i18n";

import { QueryClientProvider } from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { BrowserRouter } from "react-router";

import { App } from "@/App";
import { AppProviders, ScrollToTop } from "@/components/layout";
import { queryClient } from "@/lib/api";
import { reportClientError } from "@/lib/api/client-error-report";
import { isBrowserNoise, isExtensionError, scriptErrorDetail } from "@/lib/client-error-throttle";

// An uncaught script error or rejected promise is logged for IT as feedback
// (8 Oct); throttled per tab, and never itself an error.
window.addEventListener("error", (event) => {
  // A ResizeObserver loop warning is not a failure (21 Oct); a browser
  // extension's own script is not ours (28 Oct).
  if (isBrowserNoise(event.message) || isExtensionError(event)) {
    return;
  }
  reportClientError({
    kind: "SCRIPT",
    message: event.message || "Script error",
    // Where it happened (28 Oct): LIVE logged bare "Uncaught" with nothing to go on.
    detail: scriptErrorDetail(event),
  });
});
window.addEventListener("unhandledrejection", (event) => {
  const reason: unknown = event.reason;
  // Only a real script error: a rejected API call is the API's to log, and a
  // cancelled request is not a failure.
  if (!(reason instanceof Error) || reason.name === "AbortError") {
    return;
  }
  if (isExtensionError({ error: reason })) {
    return;
  }
  reportClientError({
    kind: "SCRIPT",
    message: `${reason.name}: ${reason.message}`,
    detail: scriptErrorDetail({ error: reason }),
  });
});

const rootElement = document.getElementById("root");
if (!rootElement) {
  throw new Error("Root element #root not found");
}

createRoot(rootElement).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <AppProviders>
        <BrowserRouter>
          {/* Inside the router, above the routes: it needs the location, and
              every page wants it. */}
          <ScrollToTop />
          <App />
        </BrowserRouter>
      </AppProviders>
      <ReactQueryDevtools initialIsOpen={false} />
    </QueryClientProvider>
  </StrictMode>,
);
